import { db } from '../config/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

// Types for the enhanced Active Negatives dashboard
export interface NegativeExactData {
  target: string;
  clicks: number;
  probability?: number;
}

export interface NegativePhraseData {
  target: string;
  clicks: number;
  probability?: number;
}

export interface ConvertedTermData {
  term: string;
  sales: number;
}

export interface ActiveNegativesSummary {
  // Existing data (unchanged)
  convertedTerms: ConvertedTermData[];
  totalConvertedTerms: number;
  brandNamesTerms: string[];
  totalBrandNamesTerms: number;
  
  // New data
  negativeExacts: NegativeExactData[];
  totalNegativeExacts: number;
  totalNegativeExactsClicks: number;
  
  negativePhrases: NegativePhraseData[];
  totalNegativePhrases: number;
  totalNegativePhrasesClicks: number;
  
  farmingGroup?: string;
}

// Firestore data interfaces
interface PMT2ProductData {
  'Farming Group'?: string;
  [key: string]: any;
}

interface NegativeUniqueCodeEntry {
  type: string;
  target: string;
}

interface NegativeUniqueCodesData {
  asin: string;
  campaign: string;
  companyName: string;
  entries: NegativeUniqueCodeEntry[];
  lastUpdated?: any;
  source?: string;
}

interface AllCstResultsData {
  term?: string;
  asin?: string;
  clicks?: number | string;
  sales?: number | string;
  stillActive?: string;
  type?: string;
  companyName?: string;
}

interface AllWordResultsData {
  asin?: string;
  companyName?: string;
  wordResultEntries?: WordResultEntry[];
  lastUpdated?: any;
  source?: string;
}

interface WordResultEntry {
  term?: string | null;
  clicks?: number | string | null;
  sales?: number | string | null;
  stillActive?: string | null;
}

interface DispositionLedgerData {
  term?: string;
  percentProbability?: number;
  asin?: string;
  cst?: string;
  companyName?: string;
}

interface ActiveTargetsHelperFirestoreData {
  asin?: string;
  companyName?: string;
  targetEntries?: TargetEntryData[];
  lastUpdated?: any;
  source?: string;
}

interface TargetEntryData {
  campaign: string | null;
  term: string | null;
  sales: number | string | null;
}

interface BrandNamesToNegOutFirestoreData {
  terms?: string[];
  lastUpdated?: any;
  source?: string;
}

// Type guards
const isPMT2ProductData = (data: unknown): data is PMT2ProductData => {
  return typeof data === 'object' && data !== null;
};

const isNegativeUniqueCodesData = (data: unknown): data is NegativeUniqueCodesData => {
  return typeof data === 'object' && data !== null && 'entries' in data && Array.isArray((data as any).entries);
};

const isAllCstResultsData = (data: unknown): data is AllCstResultsData => {
  return typeof data === 'object' && data !== null;
};

const isAllWordResultsData = (data: unknown): data is AllWordResultsData => {
  return typeof data === 'object' && data !== null && 'wordResultEntries' in data;
};

const isDispositionLedgerData = (data: unknown): data is DispositionLedgerData => {
  return typeof data === 'object' && data !== null;
};

const isActiveTargetsHelperData = (data: unknown): data is ActiveTargetsHelperFirestoreData => {
  return typeof data === 'object' && data !== null && 'targetEntries' in data && Array.isArray((data as any).targetEntries);
};

const isBrandNamesToNegOutData = (data: unknown): data is BrandNamesToNegOutFirestoreData => {
  return typeof data === 'object' && data !== null && 'terms' in data && Array.isArray((data as any).terms);
};

// Utility functions
const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const convertTargetToFirestoreKey = (target: string): string => {
  return target.toLowerCase().replace(/\s+/g, '_');
};

/**
 * Fetches the Farming Group for the selected ASIN
 */
export const fetchFarmingGroup = async (companyName: string, asin: string): Promise<string | undefined> => {
  try {
    console.log(`Fetching Farming Group for: ${companyName}/${asin}`);
    
    const productDocRef = doc(db, 'pmt2_data', companyName, 'products', asin);
    const productSnap = await getDoc(productDocRef);
    
    if (productSnap.exists()) {
      const data = productSnap.data();
      if (isPMT2ProductData(data) && data['Farming Group']) {
        console.log(`Found Farming Group: ${data['Farming Group']}`);
        return data['Farming Group'];
      }
    }
    
    console.warn(`No Farming Group found for ${companyName}/${asin}`);
    return undefined;
  } catch (error) {
    console.error('Error fetching Farming Group:', error);
    return undefined;
  }
};

/**
 * Fetches probability data from disposition ledger for a specific ASIN
 */
export const fetchDispositionProbabilities = async (companyName: string, asin: string): Promise<Map<string, number>> => {
  try {
    console.log(`Fetching disposition probabilities for: ${companyName}/${asin}`);
    
    const probabilityMap = new Map<string, number>();
    
    const asinCollectionRef = collection(db, 'pmt3_disposition_ledger', companyName, asin);
    const termsSnapshot = await getDocs(asinCollectionRef);
    
    if (termsSnapshot.empty) {
      console.warn(`No disposition data found for ${companyName}/${asin}`);
      return probabilityMap;
    }
    
    termsSnapshot.forEach(termDoc => {
      const data = termDoc.data();
      if (isDispositionLedgerData(data) && data.term && typeof data.percentProbability === 'number') {
        probabilityMap.set(data.term.toLowerCase(), data.percentProbability);
      }
    });
    
    console.log(`Found ${probabilityMap.size} probability entries for ${companyName}/${asin}`);
    return probabilityMap;
  } catch (error) {
    console.error('Error fetching disposition probabilities:', error);
    return new Map<string, number>();
  }
};

/**
 * Fetches Negative Unique Codes from Broad campaign and processes them with parallel processing
 */
export const fetchNegativeUniqueCodes = async (
  companyName: string,
  asin: string,
  probabilityMap: Map<string, number>
): Promise<{ negativeExacts: NegativeExactData[], negativePhrases: NegativePhraseData[] }> => {
  try {
    console.log(`Fetching Negative Unique Codes for: ${companyName}/${asin}`);
    
    // Fetch from /pmt3_negative_unique_codes/{Client Name}/{ASIN}/Broad
    const broadDocRef = doc(db, 'pmt3_negative_unique_codes', companyName, asin, 'Broad');
    const broadSnap = await getDoc(broadDocRef);
    
    if (!broadSnap.exists()) {
      console.warn(`No Broad campaign data found for ${companyName}/${asin}`);
      return { negativeExacts: [], negativePhrases: [] };
    }
    
    const data = broadSnap.data();
    if (!isNegativeUniqueCodesData(data) || !data.entries) {
      console.warn(`Invalid Negative Unique Codes data for ${companyName}/${asin}`);
      return { negativeExacts: [], negativePhrases: [] };
    }
    
    console.log(`Found ${data.entries.length} entries in Broad campaign`);
    
    // Filter entries: exclude "Campaign Negative Phrase" or entries containing both "Campaign" and "Negative"
    const validEntries = data.entries.filter(entry => {
      const type = entry.type.toLowerCase();
      if (type === 'campaign negative phrase') return false;
      if (type.includes('campaign') && type.includes('negative')) return false;
      return true;
    });
    
    console.log(`${validEntries.length} entries passed initial filtering`);
    
    // Separate entries by type for parallel processing
    const negativeExactEntries = validEntries.filter(entry => entry.type === 'Negative Exact');
    const negativePhrasesEntries = validEntries.filter(entry => entry.type !== 'Negative Exact');
    
    // Fetch word results data once for all negative phrases
    let wordResultsData: AllWordResultsData | null = null;
    if (negativePhrasesEntries.length > 0) {
      try {
        const wordDocRef = doc(db, 'pmt3_all_word_results', companyName, 'data', asin);
        const wordSnap = await getDoc(wordDocRef);
        if (wordSnap.exists() && isAllWordResultsData(wordSnap.data())) {
          wordResultsData = wordSnap.data() as AllWordResultsData;
        }
      } catch (error) {
        console.error('Error fetching word results data:', error);
      }
    }
    
    // Process Negative Exacts in parallel
    const negativeExactsPromises = negativeExactEntries.map(async (entry) => {
      try {
        const targetKey = convertTargetToFirestoreKey(entry.target);
        const cstDocRef = doc(db, 'pmt3_all_cst_results', companyName, asin, targetKey);
        const cstSnap = await getDoc(cstDocRef);
        
        if (cstSnap.exists()) {
          const cstData = cstSnap.data();
          if (isAllCstResultsData(cstData)) {
            const sales = parseNumericValue(cstData.sales);
            
            // Only include if sales is 0 (null counts as 0)
            if (sales === 0) {
              const clicks = parseNumericValue(cstData.clicks);
              const probability = probabilityMap.get(entry.target.toLowerCase());
              
              return {
                target: entry.target,
                clicks,
                probability: probability ? probability * 100 : undefined // Convert to percentage
              };
            }
          }
        }
        return null;
      } catch (error) {
        console.error(`Error processing Negative Exact "${entry.target}":`, error);
        return null;
      }
    });
    
    // Process Negative Phrases (using pre-fetched word results data)
    const negativePhrases: NegativePhraseData[] = [];
    if (wordResultsData && wordResultsData.wordResultEntries) {
      for (const entry of negativePhrasesEntries) {
        const matchingEntry = wordResultsData.wordResultEntries.find(
          wordEntry => wordEntry.term?.toLowerCase() === entry.target.toLowerCase()
        );
        
        if (matchingEntry) {
          const sales = parseNumericValue(matchingEntry.sales);
          
          // Only include if sales is 0 (null counts as 0)
          if (sales === 0) {
            const clicks = parseNumericValue(matchingEntry.clicks);
            const probability = probabilityMap.get(entry.target.toLowerCase());
            
            negativePhrases.push({
              target: entry.target,
              clicks,
              probability: probability ? probability * 100 : undefined // Convert to percentage
            });
          }
        }
      }
    }
    
    // Wait for all Negative Exacts to complete
    const negativeExactsResults = await Promise.all(negativeExactsPromises);
    const negativeExacts: NegativeExactData[] = negativeExactsResults.filter(result => result !== null) as NegativeExactData[];
    
    // Sort by clicks descending and limit to 190 items each
    negativeExacts.sort((a, b) => b.clicks - a.clicks);
    negativePhrases.sort((a, b) => b.clicks - a.clicks);
    
    const limitedNegativeExacts = negativeExacts.slice(0, 190);
    const limitedNegativePhrases = negativePhrases.slice(0, 190);
    
    console.log(`Processed ${limitedNegativeExacts.length} Negative Exacts and ${limitedNegativePhrases.length} Negative Phrases`);
    
    return {
      negativeExacts: limitedNegativeExacts,
      negativePhrases: limitedNegativePhrases
    };
    
  } catch (error) {
    console.error('Error fetching Negative Unique Codes:', error);
    return { negativeExacts: [], negativePhrases: [] };
  }
};

/**
 * Fetches existing converted terms data (unchanged from original implementation)
 */
export const fetchConvertedTerms = async (companyName: string, asin: string): Promise<ConvertedTermData[]> => {
  try {
    console.log(`Fetching converted terms for: ${companyName}/${asin}`);
    
    const dataDocRef = doc(db, 'pmt3_active_targets_helper', companyName, 'data', asin);
    const docSnap = await getDoc(dataDocRef);
    
    if (!docSnap.exists()) {
      console.warn(`No Active Targets Helper data found for ${companyName}/${asin}`);
      return [];
    }
    
    const data = docSnap.data();
    const convertedTerms: ConvertedTermData[] = [];
    
    if (isActiveTargetsHelperData(data) && data.targetEntries) {
      data.targetEntries.forEach(entry => {
        const sales = parseNumericValue(entry.sales);
        
        // Only include entries with sales > 0
        if (sales > 0) {
          convertedTerms.push({
            term: entry.term || '',
            sales: sales
          });
        }
      });
    }
    
    // Sort by sales descending and limit to 190 entries
    convertedTerms.sort((a, b) => b.sales - a.sales);
    return convertedTerms.slice(0, 190);
    
  } catch (error) {
    console.error('Error fetching converted terms:', error);
    return [];
  }
};

/**
 * Fetches brand names to neg out (unchanged from original implementation)
 */
export const fetchBrandNamesTerms = async (companyName: string): Promise<string[]> => {
  try {
    console.log(`Fetching brand names for: ${companyName}`);
    
    const dataDocRef = doc(db, 'pmt3_brand_names_to_neg_out', companyName, 'data', 'terms');
    const docSnap = await getDoc(dataDocRef);
    
    if (!docSnap.exists()) {
      console.warn(`No Brand Names data found for ${companyName}`);
      return [];
    }
    
    const data = docSnap.data();
    
    if (isBrandNamesToNegOutData(data) && data.terms) {
      return data.terms;
    }
    
    return [];
    
  } catch (error) {
    console.error('Error fetching brand names:', error);
    return [];
  }
};

/**
 * Main function to fetch all Active Negatives data
 */
export const fetchActiveNegatives = async (companyName: string, asin: string): Promise<ActiveNegativesSummary> => {
  try {
    console.log(`Fetching complete Active Negatives data for: ${companyName}/${asin}`);
    
    // Fetch all data in parallel where possible
    const [farmingGroup, probabilityMap, convertedTerms, brandNamesTerms] = await Promise.all([
      fetchFarmingGroup(companyName, asin),
      fetchDispositionProbabilities(companyName, asin),
      fetchConvertedTerms(companyName, asin),
      fetchBrandNamesTerms(companyName)
    ]);
    
    // Fetch negative unique codes (depends on probabilityMap)
    const { negativeExacts, negativePhrases } = await fetchNegativeUniqueCodes(companyName, asin, probabilityMap);
    
    // Calculate totals
    const totalNegativeExactsClicks = negativeExacts.reduce((sum, item) => sum + item.clicks, 0);
    const totalNegativePhrasesClicks = negativePhrases.reduce((sum, item) => sum + item.clicks, 0);
    
    const summary: ActiveNegativesSummary = {
      // Existing data (unchanged)
      convertedTerms,
      totalConvertedTerms: convertedTerms.length,
      brandNamesTerms,
      totalBrandNamesTerms: brandNamesTerms.length,
      
      // New data
      negativeExacts,
      totalNegativeExacts: negativeExacts.length,
      totalNegativeExactsClicks,
      
      negativePhrases,
      totalNegativePhrases: negativePhrases.length,
      totalNegativePhrasesClicks,
      
      farmingGroup
    };
    
    console.log(`Active Negatives summary:`, {
      convertedTerms: summary.totalConvertedTerms,
      brandNames: summary.totalBrandNamesTerms,
      negativeExacts: summary.totalNegativeExacts,
      negativePhrases: summary.totalNegativePhrases,
      farmingGroup: summary.farmingGroup
    });
    
    return summary;
    
  } catch (error) {
    console.error('Error fetching Active Negatives:', error);
    throw error;
  }
};

/**
 * Gets available companies from PMT3 collections
 */
export const getAvailableCompaniesForActiveNegatives = async (): Promise<string[]> => {
  try {
    const collectionRef = collection(db, 'pmt3_negative_unique_codes');
    const querySnapshot = await getDocs(collectionRef);
    
    const companies: string[] = [];
    querySnapshot.forEach(doc => {
      companies.push(doc.id);
    });
    
    return companies.sort();
  } catch (error) {
    console.error('Error fetching available companies for active negatives:', error);
    return [];
  }
};